/**
 * PageEnter — the "page mount" animation lifted from Kora's PageTransition.
 *
 * Fade + 20px up-nudge, 0.3s ease-out. Entry only. Runs once per mount,
 * which means it fires on every route change because both layouts key
 * their <AnimatePresence> motion container by `location.pathname` — the
 * keyed ancestor remounts its whole subtree on navigation, so we remount
 * too.
 *
 * No `exit` animation on purpose. The layout's AnimatePresence owns the
 * cross-fade (short, no Y movement — Y on exit used to leave a blank
 * scroll-slot gap during the tween; see comment in DashboardLayout.tsx).
 * This component only handles the *reveal* of new page content.
 *
 * Placement: the layouts wrap <Outlet /> in <PageEnter> so every route
 * gets it for free. Individual pages can also import this directly if
 * they need a custom root (e.g. a landing hero that animates differently
 * from the shell).
 */

import { motion } from 'framer-motion';

interface PageEnterProps {
  children: React.ReactNode;
  className?: string;
}

export function PageEnter({ children, className }: PageEnterProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
}
